import React, { PureComponent } from 'react';
import { CopyButton } from './CopyButton';
import { withTooltip } from '@eventbrite/legacy-eds.tooltip';
import { VALID_DIRECTIONS } from '@eventbrite/legacy-eds.tooltip/constants';
import {
    injectIntl,
    defineMessage,
    type MessageDescriptor,
    WrappedComponentProps,
} from 'react-intl';
import { DefaultCopyIconButton } from './DefaultCopyIconButton';

type TooltipDirection = (typeof VALID_DIRECTIONS)[number];

interface CopyButtonWithTooltipProps {
    value: string;
    tooltipId: string;
    onCopyClick?: () => void;
    tooltipArrowLocation?: TooltipDirection;
    render?: (hasCopied: boolean) => React.ReactNode;
    __tooltipContainerClassName?: string;
    tooltipText?: MessageDescriptor;
    tooltipSuccessText?: MessageDescriptor;
}

const _getTooltipText = (
    hasCopied: boolean,
    tooltipText: string,
    tooltipSuccessText: string,
): string => {
    if (hasCopied) {
        return tooltipSuccessText;
    }
    return tooltipText;
};

const CopyButtonWrapper = ({ children }: { children: React.ReactNode }) => children;
const CopyButtonWithTooltipComponentWithTooltip = withTooltip(CopyButtonWrapper);

class CopyButtonWithTooltipComponent extends PureComponent<
    CopyButtonWithTooltipProps & WrappedComponentProps
> {
    static defaultProps = {
        tooltipArrowLocation: 'bottom' as const,
        tooltipText: defineMessage({ id: 'Copy', defaultMessage: 'Copy' }),
        tooltipSuccessText: defineMessage({ id: 'Copied', defaultMessage: 'Copied' }),
        render: DefaultCopyIconButton,
    };

    onRender = (hasCopied: boolean) => {
        const {
            intl,
            tooltipText = defineMessage({ id: 'Copy', defaultMessage: 'Copy' }),
            tooltipSuccessText = defineMessage({ id: 'Copied', defaultMessage: 'Copied' }),
            tooltipArrowLocation,
            tooltipId,
            __tooltipContainerClassName,
            render = DefaultCopyIconButton,
        } = this.props;

        const tooltipTextToRender = _getTooltipText(
            hasCopied,
            intl.formatMessage(tooltipText),
            intl.formatMessage(tooltipSuccessText),
        );

        return (
            <CopyButtonWithTooltipComponentWithTooltip
                tooltipArrowLocation={tooltipArrowLocation}
                tooltipId={tooltipId}
                tooltipText={tooltipTextToRender}
                __containerClassName={__tooltipContainerClassName}
            >
                {render(hasCopied)}
            </CopyButtonWithTooltipComponentWithTooltip>
        );
    };

    render() {
        const { value, onCopyClick } = this.props;

        return <CopyButton render={this.onRender} value={value} onCopyClick={onCopyClick} />;
    }
}

export const CopyButtonWithTooltip = injectIntl(CopyButtonWithTooltipComponent);
